/**
 * zx-head-scroll.js
 * 参考 tiffany.com 的 header 滚动行为：
 *  1. .whYY-topsticky 的 sticky top 设为 -滚动公告高度（移动端 + 电脑端）：
 *     滚动公告先随页面自然滚走，滚过 annH 后导航吸顶
 *  2. 电脑端吸顶后：
 *     - 下滚超过导航高度(annH + pageheaderH) → 整体上移隐藏
 *     - 上滚 → 紧凑吸顶：隐藏 #pageheader，给 #main-nav 加 main-nav-scroll
 *     - 完全到顶部(scrollY=0) → 完全恢复
 *     移动端导航始终显示，滚动公告仅在最顶部时显示，仅同步 CSS 变量不做隐藏/显示
 */
(function () {
  'use strict';

  const mq = window.matchMedia('(min-width: 768px)');
  const HIDE_CLASS = 'zx-pageheader-hidden';
  const HOST_CLASS = 'zx-pageheader-host';
  const COLLAPSE_CLASS = 'zx-pageheader-collapsed';
  const SCROLL_CLASS = 'main-nav-scroll';

  // 注入过渡样式
  const style = document.createElement('style');
  style.textContent =
    '#pageheader,' +
    '.' + HOST_CLASS + '{' +
      'transition: all .4s ease-in-out;' +
    '}' +
    '.whYY-topsticky{' +
      'transition: transform .4s ease-in-out;' +
    '}' +
    '.' + HIDE_CLASS + '{' +
      'transform: translate3d(0, -100%, 0);' +
    '}' +
    '#pageheader.' + COLLAPSE_CLASS + ' > .logo-area{' +
      'display: none;' +
    '}';
  document.head.appendChild(style);

  let header = null;
  let parent = null;
  let target = null; // 电脑端 = .whYY-topsticky（sticky 容器），移动端 = #pageheader
  let mainNav = null;
  let announcement = null;
  let topsticky = null;
  let annH = 0;
  let navH = 0; // 滚动公告高度 + #pageheader 高度
  let headerH = 0;
  let lastScrollY = 0;
  let ticking = false;
  let scrollBound = false;
  let lastSyncKey = '';

  // 测量滚动公告高度（无公告则为 0）
  function measureAnnouncement() {
    annH = announcement ? announcement.getBoundingClientRect().height : 0;
  }

  // 测量导航高度（仅 #pageheader 未折叠时更新，折叠时用缓存值）
  function measureNav() {
    if (header && !header.classList.contains(COLLAPSE_CLASS)) {
      headerH = header.getBoundingClientRect().height;
    }
    navH = annH + headerH;
  }

  // 设置 .whYY-topsticky sticky top = -annH，让滚动公告先滚走、导航吸顶
  function applyLayout() {
    measureAnnouncement();
    measureNav();
    if (topsticky) topsticky.style.top = -annH + 'px';
    lastSyncKey = ''; // 尺寸可能变化，强制重算
    syncHeaderVars();
  }

  // 根据 header 当前状态同步 html 上的 3 个高度 CSS 变量：
  //   --theme-header-height / --theme-sticky-header-height：可见 header 高度
  //   --theme-sticky-headerbar-height：滚动公告高度（不可见时为 0）
  // PC 端按隐藏/紧凑 class 计算；移动端按滚动位置计算（导航始终显示，公告仅在最顶部可见）
  function syncHeaderVars() {
    if (!header || !target) return;
    const html = document.documentElement;
    let h1, h3, key;

    if (mq.matches) {
      // PC 端：根据隐藏/紧凑状态计算
      const hidden = target.classList.contains(HIDE_CLASS);
      const collapsed = header.classList.contains(COLLAPSE_CLASS);
      key = 'pc-' + (hidden ? '1' : '0') + (collapsed ? '1' : '0');
      if (key === lastSyncKey) return; // 状态未变，跳过重算
      lastSyncKey = key;

      if (hidden) {
        // 整体上移隐藏
        h1 = 0;
        h3 = 0;
      } else if (collapsed) {
        // 紧凑吸顶：滚动公告已随页面滚走
        h1 = (topsticky ? topsticky.offsetHeight : 0) - annH;
        h3 = 0;
      } else {
        // 完全显示：滚动公告可见
        h1 = topsticky ? topsticky.offsetHeight : 0;
        h3 = annH;
      }
    } else {
      // 移动端：导航始终显示，滚动公告仅在最顶部(scrollY<=42)时显示
      const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
      const atTop = currentScrollY <= 42;
      key = 'mobile-' + (atTop ? 'top' : 'scrolled');
      if (key === lastSyncKey) return; // 状态未变，跳过重算
      lastSyncKey = key;

      if (atTop) {
        // 滚动公告 + 导航均显示
        h1 = topsticky ? topsticky.offsetHeight : 0;
        h3 = annH;
      } else {
        // 仅导航显示，滚动公告已随页面滚走
        h1 = topsticky ? (topsticky.offsetHeight - annH) : 0;
        h3 = 0;
      }
    }

    html.style.setProperty('--theme-header-height', h1 + 'px');
    html.style.setProperty('--theme-sticky-header-height', h1 + 'px');
    html.style.setProperty('--theme-sticky-headerbar-height', h3 + 'px');
  }

  // 供 main.js 的 setHeaderHeightProperty 委托调用（强制重算）
  window.zxSyncHeaderHeight = function () {
    lastSyncKey = '';
    syncHeaderVars();
  };

  // 确定操作目标并清除残留 class
  function setupTarget() {
    if (header) header.classList.remove(HIDE_CLASS);
    if (parent) parent.classList.remove(HIDE_CLASS);
    if (topsticky) topsticky.classList.remove(HIDE_CLASS);
    target = (mq.matches && topsticky) ? topsticky : header;
  }

  function update() {
    const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;

    if (!mq.matches) {
      // 移动端：导航始终显示，仅同步 CSS 变量（滚动公告随滚动出现/消失）
      syncHeaderVars();
      lastScrollY = currentScrollY;
      ticking = false;
      return;
    }

    const delta = currentScrollY - lastScrollY;

    if (currentScrollY <= 42) {
      if (header.classList.contains(COLLAPSE_CLASS)) {
        // 从紧凑模式恢复：滚动公告+导航作为一体瞬间隐藏 → 切布局 → 带过渡一起滑下
        topsticky.style.transition = 'none';
        topsticky.classList.add(HIDE_CLASS);
        header.classList.remove(COLLAPSE_CLASS);
        if (mainNav) mainNav.classList.remove(SCROLL_CLASS);
        topsticky.offsetHeight; // 强制回流，让无过渡隐藏生效
        topsticky.style.transition = ''; // 恢复过渡
        topsticky.classList.remove(HIDE_CLASS); // 一起滑下
      } else {
        target.classList.remove(HIDE_CLASS);
      }
    } else if (delta > 0) {
      // 向下滚
      if (currentScrollY > navH && lastScrollY > navH) {
        target.classList.add(HIDE_CLASS);
      } else if (lastScrollY <= navH && currentScrollY > navH) {
        target.classList.add(HIDE_CLASS);
      }
    } else if (delta < 0) {
      // 向上滚 → 从隐藏恢复为紧凑吸顶（仅当当前处于隐藏状态时才触发，避免来回波动）
      if (target.classList.contains(HIDE_CLASS)) {
        target.classList.remove(HIDE_CLASS);
        header.classList.add(COLLAPSE_CLASS);
        if (mainNav) mainNav.classList.add(SCROLL_CLASS);
      }
    }

    syncHeaderVars();
    lastScrollY = currentScrollY;
    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(update);
      ticking = true;
    }
  }

  function bindScroll() {
    if (scrollBound) return;
    lastScrollY = window.pageYOffset || document.documentElement.scrollTop;
    window.addEventListener('scroll', onScroll, { passive: true });
    scrollBound = true;
  }

  function unbindScroll() {
    if (!scrollBound) return;
    window.removeEventListener('scroll', onScroll);
    scrollBound = false;
  }

  function onMqChange(e) {
    setupTarget();
    applyLayout();
    lastScrollY = window.pageYOffset || document.documentElement.scrollTop;
    // PC 与移动端均监听滚动（PC 做隐藏/紧凑切换，移动端仅同步 CSS 变量）
    bindScroll();
    if (!e.matches) {
      // 切到移动端：清除 PC 端残留状态
      if (target) target.classList.remove(HIDE_CLASS);
      if (topsticky) topsticky.classList.remove(HIDE_CLASS);
      if (header) header.classList.remove(COLLAPSE_CLASS);
      if (mainNav) mainNav.classList.remove(SCROLL_CLASS);
    }
    lastSyncKey = '';
    syncHeaderVars();
  }

  function init() {
    header = document.getElementById('pageheader');
    if (!header) return;
    parent = header.parentElement;
    if (parent) parent.classList.add(HOST_CLASS);
    mainNav = document.getElementById('main-nav');
    announcement = document.querySelector('.whyyannouncementbar');
    topsticky = document.querySelector('.whYY-topsticky');

    setupTarget();
    applyLayout();

    window.addEventListener('resize', applyLayout);
    mq.addEventListener('change', onMqChange);

    // PC 做隐藏/紧凑切换，移动端仅同步 CSS 变量，两端均监听滚动
    bindScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
